// Lo elegido en el plano para asignar tarifas: filas, mesas o las dos cosas
// (de una o varias ubicaciones). Cada elemento es { tipo: "fila", sector,
// indice } o { tipo: "mesa", sector, clave }.
import { cajaDecorativo } from "./geometria";
import { quitarDecorativo, quitarSector, sePuedeBorrar } from "./mapa";

// elementosDe: la lista de lo elegido, sea cual sea la forma de la selección
export const elementosDe = (seleccion) => {
  if (seleccion?.tipo === "lugares") return seleccion.elementos;
  if (seleccion?.tipo !== "fila" && seleccion?.tipo !== "mesa") return [];
  const lista =
    seleccion.elementos ||
    [seleccion.tipo === "fila" ? { sector: seleccion.sector, indice: seleccion.indice } : { sector: seleccion.sector, clave: seleccion.clave }];
  return lista.map((e) => ({ ...e, tipo: seleccion.tipo }));
};

export const mismoElemento = (a, b) =>
  a.tipo === b.tipo && a.sector === b.sector && (a.tipo === "fila" ? a.indice === b.indice : a.clave === b.clave);

// seleccionDe arma la selección. Con un solo tipo guarda además el primer
// elemento y los de su ubicación (lo usan el panel y las manijas).
export const seleccionDe = (elementos) => {
  if (elementos.length === 0) return null;
  if (new Set(elementos.map((e) => e.tipo)).size > 1) return { tipo: "lugares", elementos };
  const tipo = elementos[0].tipo;
  const lista = elementos.map(({ tipo: _tipo, ...e }) => e);
  const [primero] = lista;
  const mismos = lista.filter((e) => e.sector === primero.sector);
  return tipo === "fila"
    ? { tipo, sector: primero.sector, indice: primero.indice, indices: mismos.map((e) => e.indice), elementos: lista }
    : { tipo, sector: primero.sector, clave: primero.clave, claves: mismos.map((e) => e.clave), elementos: lista };
};

// alternar: con Shift, suma el elemento o lo saca si ya estaba
export const alternar = (seleccion, elemento) => {
  const actuales = elementosDe(seleccion);
  const elementos = actuales.some((e) => mismoElemento(e, elemento))
    ? actuales.filter((e) => !mismoElemento(e, elemento))
    : [...actuales, elemento];
  return seleccionDe(elementos);
};

const girar = (x, y, grados) => {
  const r = ((grados || 0) * Math.PI) / 180;
  return { x: x * Math.cos(r) - y * Math.sin(r), y: x * Math.sin(r) + y * Math.cos(r) };
};

const enPlano = (sector, x, y) => {
  const p = girar(x, y, sector.rotacion);
  return { x: sector.x + p.x, y: sector.y + p.y };
};

// lugaresEnArea: las filas con algún asiento y las mesas con el centro dentro
// del rectángulo (en coordenadas del plano). Lo que tiene algo vendido no se
// puede cambiar de tarifa: se cuenta aparte.
export const lugaresEnArea = (mapa, area) => {
  const minX = Math.min(area.x1, area.x2);
  const maxX = Math.max(area.x1, area.x2);
  const minY = Math.min(area.y1, area.y2);
  const maxY = Math.max(area.y1, area.y2);
  const dentro = (p) => p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
  const elementos = [];
  let conVentas = 0;
  mapa.sectores
    .filter((s) => s.en_mapa)
    .forEach((s) => {
      s.filas.forEach((f, indice) => {
        if (!f.asientos.some((a) => dentro(enPlano(s, a.x, a.y)))) return;
        if (f.asientos.some((a) => a.vendido)) conVentas++;
        else elementos.push({ tipo: "fila", sector: s.clave, indice });
      });
      s.mesas.forEach((m) => {
        const c = girar(m.ancho / 2, m.alto / 2, m.rotacion);
        if (!dentro(enPlano(s, m.x + c.x, m.y + c.y))) return;
        if (m.vendida || m.sillas.some((x) => x.vendido)) conVentas++;
        else elementos.push({ tipo: "mesa", sector: s.clave, clave: m.clave });
      });
    });
  return { elementos, conVentas };
};

// sumarElementos: une dos listas sin repetir
export const sumarElementos = (a, b) => [...a, ...b.filter((e) => !a.some((x) => mismoElemento(x, e)))];

// resumen: "3 filas y 2 mesas"
export const resumen = (elementos) => {
  const filas = elementos.filter((e) => e.tipo === "fila").length;
  const mesas = elementos.filter((e) => e.tipo === "mesa").length;
  const texto = (n, una, varias) => (n === 1 ? `1 ${una}` : `${n} ${varias}`);
  return [filas && texto(filas, "fila", "filas"), mesas && texto(mesas, "mesa", "mesas")].filter(Boolean).join(" y ");
};

// En el diseño se eligen ubicaciones y objetos enteros: { tipo: "sector" |
// "decorativo", clave }. Varios juntos son { tipo: "varios", elementos }.
export const objetosDe = (seleccion) => {
  if (seleccion?.tipo === "varios") return seleccion.elementos;
  if (seleccion?.tipo === "sector" || seleccion?.tipo === "decorativo") return [{ tipo: seleccion.tipo, clave: seleccion.clave }];
  return [];
};

export const seleccionDeObjetos = (elementos) => {
  if (elementos.length === 0) return null;
  if (elementos.length === 1) return { tipo: elementos[0].tipo, clave: elementos[0].clave };
  return { tipo: "varios", elementos };
};

const mismoObjeto = (a, b) => a.tipo === b.tipo && a.clave === b.clave;

export const estaElegido = (seleccion, tipo, clave) => objetosDe(seleccion).some((e) => mismoObjeto(e, { tipo, clave }));

export const alternarObjeto = (seleccion, objeto) => {
  const actuales = objetosDe(seleccion);
  return seleccionDeObjetos(
    actuales.some((e) => mismoObjeto(e, objeto)) ? actuales.filter((e) => !mismoObjeto(e, objeto)) : [...actuales, objeto]
  );
};

export const sumarObjetos = (a, b) => [...a, ...b.filter((e) => !a.some((x) => mismoObjeto(x, e)))];

const cajaGirada = (x, y, ancho, alto, rotacion) => {
  const puntos = [[0, 0], [ancho, 0], [0, alto], [ancho, alto]].map(([dx, dy]) => {
    const p = girar(dx, dy, rotacion);
    return { x: x + p.x, y: y + p.y };
  });
  const xs = puntos.map((p) => p.x);
  const ys = puntos.map((p) => p.y);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
};

// objetosEnArea: ubicaciones del plano y objetos que el rectángulo toca
export const objetosEnArea = (mapa, area) => {
  const a = { minX: Math.min(area.x1, area.x2), maxX: Math.max(area.x1, area.x2), minY: Math.min(area.y1, area.y2), maxY: Math.max(area.y1, area.y2) };
  const toca = (c) => c.minX <= a.maxX && c.maxX >= a.minX && c.minY <= a.maxY && c.maxY >= a.minY;
  const sectores = mapa.sectores
    .filter((s) => s.en_mapa && toca(cajaGirada(s.x, s.y, s.ancho, s.alto, s.rotacion)))
    .map((s) => ({ tipo: "sector", clave: s.clave }));
  const decorativos = mapa.decorativos
    .filter((d) => toca(cajaDecorativo(d)))
    .map((d) => ({ tipo: "decorativo", clave: d.clave }));
  return [...sectores, ...decorativos];
};

const moverDecorativo = (d, dx, dy) => ({
  ...d,
  x: d.x + dx,
  y: d.y + dy,
  ...(d.tipo === "linea" ? { punto_final_x: (d.punto_final_x ?? d.x) + dx, punto_final_y: (d.punto_final_y ?? d.y) + dy } : {}),
});

// moverObjetos corre todo lo elegido lo mismo
export const moverObjetos = (mapa, elementos, dx, dy) => {
  const claves = (tipo) => new Set(elementos.filter((e) => e.tipo === tipo).map((e) => e.clave));
  const sectores = claves("sector");
  const decorativos = claves("decorativo");
  return {
    ...mapa,
    sectores: mapa.sectores.map((s) => (sectores.has(s.clave) ? { ...s, x: s.x + dx, y: s.y + dy } : s)),
    decorativos: mapa.decorativos.map((d) => (decorativos.has(d.clave) ? moverDecorativo(d, dx, dy) : d)),
  };
};

// motivoNoBorrarSector: con ventas, o con tarifas (salvo una parte, que vende las de su ubicación)
export const motivoNoBorrarSector = (sector) => {
  if (!sePuedeBorrar(sector)) return "tiene entradas vendidas";
  if (sector.tarifas.length > 0 && !sector.editor?.parteDe) return "tiene tarifas asignadas";
  return null;
};

// quitarObjetos borra lo elegido que se pueda y devuelve los nombres de lo que no
export const quitarObjetos = (mapa, elementos) => {
  const omitidos = [];
  let m = mapa;
  elementos.forEach((e) => {
    if (e.tipo === "decorativo") {
      m = quitarDecorativo(m, e.clave);
      return;
    }
    const s = m.sectores.find((x) => x.clave === e.clave);
    if (!s) return;
    const motivo = motivoNoBorrarSector(s);
    if (motivo) omitidos.push(`${s.nombre} (${motivo})`);
    else m = quitarSector(m, e.clave);
  });
  return { mapa: m, omitidos };
};
